import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { useNavigate, createSearchParams } from 'react-router';
import { App, type InputRef } from 'antd';
import { useTranslation } from 'translations/hooks';
import { Routes } from 'business/router/constants';
import { getLocalStorageValue } from 'technical/local-storage';
import {
  SCAN_PATTERN_KEY,
  SCAN_SITE_KEY,
} from 'technical/local-storage/constants';
import {
  scanPatternSchema,
  scanSiteSchema,
  type ScanPatternSchema,
  type ScanSiteSchema,
} from 'technical/local-storage/types';
import { useGetSegmentData } from './data/use-get-segment';
import { SegmentStatus } from 'business/receipt/constants';

export const useReceiptScanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [inputValue, setInputValue] = useState('');
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [quarantineDrawerIsOpen, setQuarantineDrawerIsOpen] = useState(false);
  const [searchSerialNumber, setSearchSerialNumber] = useState<string>('');
  const [quarantineRedirection, setQuarantineRedirection] =
    useState<string>('');
  const [repairRedirection, setRepairRedirection] = useState<string>('');
  const [rejectRedirection, setRejectRedirection] = useState<string>('');
  const inputRef = useRef<InputRef>(null);

  const scanPattern = getLocalStorageValue<ScanPatternSchema>({
    key: SCAN_PATTERN_KEY,
    schema: scanPatternSchema,
  });

  const scanSite = getLocalStorageValue<ScanSiteSchema>({
    key: SCAN_SITE_KEY,
    schema: scanSiteSchema,
  });
  const isQuarantineEnabled = scanSite?.isQuarantineEnabled ?? true;

  const {
    data: segmentData,
    isLoading,
    isNotFound,
    isServerError,
  } = useGetSegmentData(searchSerialNumber);

  const submitSearch = useCallback(
    (rawSerialNumber: string) => {
      if (
        !rawSerialNumber ||
        !scanPattern ||
        rawSerialNumber.length < scanPattern.extractLength
      ) {
        message.warning(
          t('receipt.wrongSerialNumber', { value: rawSerialNumber }),
        );
        setInputValue('');
        return;
      }

      // the serial number is the exact value scanned
      if (rawSerialNumber.length === scanPattern.extractLength) {
        setSearchSerialNumber(rawSerialNumber);
        setInputValue('');
        return;
      }

      const serialNumber = rawSerialNumber.slice(
        scanPattern.startIndex,
        scanPattern.startIndex + scanPattern.extractLength,
      );

      if (inputRef.current?.input) {
        inputRef.current.input.value = '';
      }

      setSearchSerialNumber(serialNumber);
      setInputValue('');
    },
    [scanPattern, message, t],
  );

  const resetAndFocus = useCallback(() => {
    setSearchSerialNumber('');
    setInputValue('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  useEffect(() => {
    if (segmentData) {
      const params = {
        id: segmentData.id,
        serialNumber: segmentData.serialNumber,
        ...(segmentData.segmentType !== null && {
          segmentType: segmentData.segmentType,
        }),
        ...(segmentData.manufactureDate !== null && {
          manufactureDate: segmentData.manufactureDate,
        }),
        ...(segmentData.mold !== null && {
          mold: segmentData.mold,
        }),
        status: segmentData.status,
        ...(segmentData.scanSite !== null && {
          scanSite: segmentData.scanSite,
        }),
      };

      if (segmentData.status === SegmentStatus.Quarantine) {
        setQuarantineRedirection(
          `${Routes.ReceiptChooseSegmentStatus}?${createSearchParams({ ...params, ...{ defaultStatus: SegmentStatus.Quarantine } })}`,
        );
        setRepairRedirection(
          `${Routes.ReceiptRepairPending}?${createSearchParams(params)}`,
        );
        setRejectRedirection(
          `${Routes.ReceiptChooseSegmentStatus}?${createSearchParams({ ...params, ...{ defaultStatus: SegmentStatus.Rejected } })}`,
        );
        setQuarantineDrawerIsOpen(true);
        return;
      }

      if (segmentData.status === SegmentStatus.Installed) {
        setDrawerIsOpen(true);
        return;
      }

      if (
        [SegmentStatus.Rejected, SegmentStatus.TunnelRejected].includes(
          segmentData.status as SegmentStatus,
        )
      ) {
        message.warning(
          t('receipt.segmentRejected', {
            scanSite: segmentData.scanSite ?? '-',
          }),
        );
      }

      navigate(
        `${Routes.ReceiptChooseSegmentStatus}?${createSearchParams(params)}`,
      );
    } else if (isNotFound) {
      message.info(t('receipt.newSegment'));
      navigate(
        `${Routes.ReceiptChooseSegmentStatus}?${createSearchParams({ serialNumber: searchSerialNumber })}`,
      );
    } else if (isServerError) {
      message.error(t('common.technicalError'));
    }
  }, [
    segmentData,
    isNotFound,
    isServerError,
    searchSerialNumber,
    navigate,
    message,
    t,
  ]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (scanPattern && e.target.value.length >= scanPattern.extractLength) {
      submitSearch(e.target.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const value = inputRef.current?.input?.value;
      if (value) {
        submitSearch(value);
      }
    }
  };

  return {
    inputValue,
    inputRef,
    handleInputChange,
    handleKeyDown,
    isLoading,
    drawerIsOpen,
    setDrawerIsOpen,
    quarantineDrawerIsOpen,
    setQuarantineDrawerIsOpen,
    quarantineRedirection,
    repairRedirection,
    rejectRedirection,
    resetAndFocus,
    isQuarantineEnabled,
  };
};
